import { useCallback, useEffect, useState } from "react";
import type { InsightPatientRow } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

function itemMatchesFilters(
  item: InsightPatientRow["items"][number],
  statusKeys: string[],
  itemKeys: string[],
  secondaryKeys: string[]
): boolean {
  const statusOk =
    statusKeys.length === 0 || statusKeys.includes(item.statusKey);
  const itemOk = itemKeys.length === 0 || itemKeys.includes(item.itemKey);
  const secondaryOk =
    secondaryKeys.length === 0 ||
    (item.secondaryKey != null && secondaryKeys.includes(item.secondaryKey));
  return statusOk && itemOk && secondaryOk;
}

export function useInsightSelection(filterResetKey?: string) {
  const [statusKeys, setStatusKeys] = useState<string[]>([]);
  const [itemKeys, setItemKeys] = useState<string[]>([]);
  const [secondaryKeys, setSecondaryKeys] = useState<string[]>([]);

  const toggleStatus = useCallback((key: string) => {
    setStatusKeys((prev) =>
      prev.includes(key) ? prev.filter((entry) => entry !== key) : [...prev, key]
    );
  }, []);

  const toggleItem = useCallback((key: string) => {
    setItemKeys((prev) =>
      prev.includes(key) ? prev.filter((entry) => entry !== key) : [...prev, key]
    );
  }, []);

  const toggleSecondary = useCallback((key: string) => {
    setSecondaryKeys((prev) =>
      prev.includes(key) ? prev.filter((entry) => entry !== key) : [...prev, key]
    );
  }, []);

  const clear = useCallback(() => {
    setStatusKeys([]);
    setItemKeys([]);
    setSecondaryKeys([]);
  }, []);

  useEffect(() => {
    setStatusKeys([]);
    setItemKeys([]);
    setSecondaryKeys([]);
  }, [filterResetKey]);

  const filterRows = useCallback(
    (rows: InsightPatientRow[]): InsightPatientRow[] => {
      const hasFilter =
        statusKeys.length > 0 ||
        itemKeys.length > 0 ||
        secondaryKeys.length > 0;
      if (!hasFilter) {
        return rows;
      }

      return rows
        .map((row) => ({
          ...row,
          items: row.items.filter((item) =>
            itemMatchesFilters(item, statusKeys, itemKeys, secondaryKeys)
          ),
        }))
        .filter((row) => row.items.length > 0);
    },
    [statusKeys, itemKeys, secondaryKeys]
  );

  return {
    statusKeys,
    itemKeys,
    secondaryKeys,
    toggleStatus,
    toggleItem,
    toggleSecondary,
    clear,
    filterRows,
  };
}
